<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import categoryService from '../services/category.service'
import playerImportService from '../services/playerImport.service'
import {
  loadImportState,
  saveImportState,
  clearImportState,
  validateRow,
  toImportPayload,
  type PlayerImportState,
  type ReviewRow,
} from '../composables/usePlayerImport'

const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const clubUuid = computed(() => authStore.initData?.club?.uuid || '')

const state = ref<PlayerImportState | null>(null)
const categories = ref<Array<{ uuid: string, name: string }>>([])
const sendInvitations = ref(true)
const onlyWithErrors = ref(false)
const importing = ref(false)

// Bulk actions
const showBulkActionsDropdown = ref(false)
const showGenderModal = ref(false)
const showCategoryModal = ref(false)
const bulkGender = ref<'' | 'M' | 'W'>('')
const bulkCategory = ref('')

const reviewRows = computed<ReviewRow[]>(() => state.value?.reviewRows || [])

const rowErrors = computed(() => {
  const map = new Map<number, ReturnType<typeof validateRow>>()
  reviewRows.value.forEach(row => map.set(row.id, validateRow(row)))
  return map
})
const hasError = (row: ReviewRow, field: keyof ReviewRow) => !!rowErrors.value.get(row.id)?.[field]
const rowHasErrors = (row: ReviewRow) => Object.keys(rowErrors.value.get(row.id) || {}).length > 0

const errorsCount = computed(() => reviewRows.value.filter(rowHasErrors).length)
const missingGenderCount = computed(() => reviewRows.value.filter(r => !r.gender).length)
const duplicatesCount = computed(() => reviewRows.value.filter(r => r.duplicate).length)
const invitationsCount = computed(() => reviewRows.value.filter(r => r.email).length)

const visibleRows = computed(() => (onlyWithErrors.value ? reviewRows.value.filter(rowHasErrors) : reviewRows.value))

const selectedRows = computed(() => reviewRows.value.filter(r => r.selected))
const allVisibleSelected = computed(() => visibleRows.value.length > 0 && visibleRows.value.every(r => r.selected))

const toggleSelectAll = () => {
  const value = !allVisibleSelected.value
  visibleRows.value.forEach(r => { r.selected = value })
}

const openGenderModal = () => {
  bulkGender.value = ''
  showBulkActionsDropdown.value = false
  showGenderModal.value = true
}

const openCategoryModal = () => {
  bulkCategory.value = ''
  showBulkActionsDropdown.value = false
  showCategoryModal.value = true
}

const saveBulkGender = () => {
  if (!bulkGender.value) return
  selectedRows.value.forEach(r => { r.gender = bulkGender.value })
  showGenderModal.value = false
}

const saveBulkCategory = () => {
  selectedRows.value.forEach(r => { r.category_uuid = bulkCategory.value })
  showCategoryModal.value = false
}

const removeRow = (row: ReviewRow) => {
  if (!state.value?.reviewRows) return
  state.value.reviewRows = state.value.reviewRows.filter(r => r.id !== row.id)
}

const removeSelected = () => {
  if (!state.value?.reviewRows) return
  state.value.reviewRows = state.value.reviewRows.filter(r => !r.selected)
  showBulkActionsDropdown.value = false
}

const submitImport = async () => {
  if (!state.value || errorsCount.value > 0 || !reviewRows.value.length) return

  importing.value = true
  try {
    const response = await playerImportService.importPlayers(clubUuid.value, {
      source: state.value.source,
      send_invitations: sendInvitations.value,
      players: reviewRows.value.map(toImportPayload),
    })
    clearImportState()
    state.value = null
    toastStore.success(response.message || t('playerImport.queued'))
    router.push('/dashboard')
  } catch (err: any) {
    console.error('Import players error:', err)
    const errors = err.response?.data?.errors
    const firstError = errors ? (Object.values(errors)[0] as string[])?.[0] : null
    toastStore.error(firstError || err.response?.data?.message || t('playerImport.importError'))
  } finally {
    importing.value = false
  }
}

// Persist edits (debounced) so F5 does not lose them
let saveTimeout: ReturnType<typeof setTimeout> | null = null
watch(
  () => state.value?.reviewRows,
  () => {
    if (saveTimeout) clearTimeout(saveTimeout)
    saveTimeout = setTimeout(() => {
      if (state.value) saveImportState(state.value)
    }, 500)
  },
  { deep: true }
)

const closeDropdown = () => { showBulkActionsDropdown.value = false }

// Horizontal scroll hint (shadows on table edges)
const tableScroll = ref<HTMLElement | null>(null)
const canScrollLeft = ref(false)
const canScrollRight = ref(false)
let resizeObserver: ResizeObserver | null = null

const updateScrollShadows = () => {
  const el = tableScroll.value
  if (!el) return
  canScrollLeft.value = el.scrollLeft > 0
  canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
}

watch(tableScroll, el => {
  resizeObserver?.disconnect()
  if (!el) return
  resizeObserver = new ResizeObserver(updateScrollShadows)
  resizeObserver.observe(el)
  updateScrollShadows()
})

onMounted(async () => {
  state.value = loadImportState(clubUuid.value)
  if (!state.value?.reviewRows) {
    router.replace(state.value ? '/players/import/mapping' : '/dashboard')
    return
  }

  // Default gender: M
  state.value.reviewRows.forEach(r => { if (!r.gender) r.gender = 'M' })

  document.addEventListener('click', closeDropdown)

  try {
    categories.value = await categoryService.getCategoriesForFilters(clubUuid.value)
  } catch (err) {
    console.error('Fetch categories error:', err)
  }
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  document.removeEventListener('click', closeDropdown)
  if (saveTimeout) clearTimeout(saveTimeout)
  if (state.value) saveImportState(state.value)
})

const inputClass = (error: boolean) => [
  'w-full px-2 py-1 text-sm border-2 rounded focus:outline-none focus:border-blue-600',
  error ? 'border-red-500 bg-red-50' : 'border-gray-300',
]
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div v-if="state" class="w-full px-4 py-4 md:py-4 py-2">
      <div class="mb-4 flex justify-between items-center gap-2">
        <div>
          <h1 class="text-2xl font-bold text-gray-900">{{ t('playerImport.reviewTitle') }}</h1>
          <p class="text-sm text-gray-600">
            {{ t('playerImport.summary', { count: reviewRows.length }) }}
            <span v-if="errorsCount" class="text-red-600">· {{ t('playerImport.summaryErrors', { count: errorsCount }) }}</span>
            <span v-if="missingGenderCount" class="text-red-600">· {{ t('playerImport.summaryMissingGender', { count: missingGenderCount }) }}</span>
            <span v-if="duplicatesCount" class="text-yellow-700">· {{ t('playerImport.summaryDuplicates', { count: duplicatesCount }) }}</span>
          </p>
        </div>
        <router-link to="/players/import/mapping" class="text-sm text-blue-600 hover:text-blue-800 hover:underline">
          {{ t('playerImport.backToMapping') }}
        </router-link>
      </div>

      <!-- Bulk Actions Bar -->
      <div class="mb-4 flex flex-wrap items-center gap-3">
        <div v-if="selectedRows.length" class="relative">
          <button
            class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 flex items-center gap-2"
            @click.stop="showBulkActionsDropdown = !showBulkActionsDropdown"
          >
            <span>{{ t('dashboard.optionsForSelected') }} ({{ selectedRows.length }})</span>
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
          <div
            v-show="showBulkActionsDropdown"
            class="absolute left-0 mt-2 w-64 bg-white border-2 border-gray-300 rounded shadow-lg z-50"
            @click.stop
          >
            <button class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" @click="openGenderModal">
              {{ t('playerImport.bulkGender') }}
            </button>
            <button class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" @click="openCategoryModal">
              {{ t('playerImport.bulkCategory') }}
            </button>
            <button class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100" @click="removeSelected">
              {{ t('playerImport.bulkRemove') }}
            </button>
          </div>
        </div>

        <label class="flex items-center gap-2 text-sm text-gray-700">
          <input v-model="onlyWithErrors" type="checkbox" class="w-4 h-4">
          {{ t('playerImport.onlyWithErrors') }}
        </label>
      </div>

      <!-- Table -->
      <p v-if="canScrollLeft || canScrollRight" class="mb-1 text-xs text-gray-500 text-right">
        {{ t('playerImport.scrollHint') }} →
      </p>
      <div class="relative bg-white border-2 border-gray-300 rounded shadow-sm">
        <!-- Scroll shadows -->
        <div
          class="pointer-events-none absolute inset-y-0 left-0 w-6 z-10 bg-gradient-to-r from-black/15 to-transparent transition-opacity"
          :class="canScrollLeft ? 'opacity-100' : 'opacity-0'"
        ></div>
        <div
          class="pointer-events-none absolute inset-y-0 right-0 w-6 z-10 bg-gradient-to-l from-black/15 to-transparent transition-opacity"
          :class="canScrollRight ? 'opacity-100' : 'opacity-0'"
        ></div>
        <div ref="tableScroll" class="overflow-x-auto" @scroll="updateScrollShadows">
        <table class="min-w-full text-sm">
          <thead class="bg-gray-50 border-b-2 border-gray-300">
            <tr class="text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
              <th class="px-2 py-2">
                <input type="checkbox" class="w-4 h-4" :checked="allVisibleSelected" @change="toggleSelectAll">
              </th>
              <th class="px-2 py-2 min-w-[160px]">{{ t('playerImport.category') }}</th>
              <th class="px-2 py-2">{{ t('playerImport.gender') }} <span class="text-red-600">*</span></th>
              <th class="px-2 py-2 min-w-[130px]">{{ t('playerImport.fields.first_name') }} <span class="text-red-600">*</span></th>
              <th class="px-2 py-2 min-w-[150px]">{{ t('playerImport.fields.last_name') }} <span class="text-red-600">*</span></th>
              <th class="px-2 py-2 min-w-[140px]">{{ t('playerImport.fields.birth_date') }}</th>
              <th class="px-2 py-2 min-w-[200px]">{{ t('playerImport.fields.email') }}</th>
              <th class="px-2 py-2 min-w-[140px]">{{ t('playerImport.fields.phone') }}</th>
              <th class="px-2 py-2 min-w-[130px]">{{ t('playerImport.fields.guardian1_first_name') }}</th>
              <th class="px-2 py-2 min-w-[150px]">{{ t('playerImport.fields.guardian1_last_name') }}</th>
              <th class="px-2 py-2 min-w-[110px]">{{ t('playerImport.fields.license_number') }}</th>
              <th class="px-2 py-2 min-w-[140px]">{{ t('playerImport.fields.medical_exam_date') }}</th>
              <th class="px-2 py-2 min-w-[150px]">{{ t('playerImport.fields.street') }}</th>
              <th class="px-2 py-2 min-w-[90px]">{{ t('playerImport.buildingNumber') }}</th>
              <th class="px-2 py-2 min-w-[90px]">{{ t('playerImport.fields.postal_code') }}</th>
              <th class="px-2 py-2 min-w-[130px]">{{ t('playerImport.fields.city') }}</th>
              <th class="px-2 py-2 min-w-[130px]">{{ t('playerImport.fields.school_name') }}</th>
              <th class="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr
              v-for="row in visibleRows"
              :key="row.id"
              :class="row.selected ? 'bg-blue-50' : (row.duplicate ? 'bg-yellow-50' : '')"
            >
              <td class="px-2 py-2 align-top">
                <input v-model="row.selected" type="checkbox" class="w-4 h-4 mt-1.5">
              </td>
              <td class="px-2 py-2 align-top">
                <select v-model="row.category_uuid" :class="inputClass(false)">
                  <option value="">{{ t('playerImport.noCategory') }}</option>
                  <option v-for="category in categories" :key="category.uuid" :value="category.uuid">{{ category.name }}</option>
                </select>
              </td>
              <td class="px-2 py-2 align-top">
                <div class="flex gap-1 mt-0.5" role="radiogroup" :aria-label="t('playerImport.gender')">
                  <button
                    type="button"
                    role="radio"
                    :aria-checked="row.gender === 'M'"
                    :title="t('players.male')"
                    class="px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded border-2 transition-opacity cursor-pointer"
                    :class="row.gender === 'M' ? 'border-blue-500' : 'border-transparent opacity-30 hover:opacity-60'"
                    @click="row.gender = 'M'"
                  >
                    M
                  </button>
                  <button
                    type="button"
                    role="radio"
                    :aria-checked="row.gender === 'W'"
                    :title="t('players.female')"
                    class="px-2.5 py-1 bg-pink-100 text-pink-700 text-xs font-medium rounded border-2 transition-opacity cursor-pointer"
                    :class="row.gender === 'W' ? 'border-pink-500' : 'border-transparent opacity-30 hover:opacity-60'"
                    @click="row.gender = 'W'"
                  >
                    K
                  </button>
                </div>
              </td>
              <td class="px-2 py-2 align-top">
                <input v-model="row.first_name" type="text" :class="inputClass(hasError(row, 'first_name'))">
                <span
                  v-if="row.duplicate"
                  class="mt-1 inline-block px-1.5 py-0.5 text-xs font-medium rounded bg-yellow-200 text-yellow-800 whitespace-nowrap"
                  :title="t('playerImport.duplicateHint')"
                >
                  {{ t('playerImport.possibleDuplicate') }}
                </span>
              </td>
              <td class="px-2 py-2 align-top"><input v-model="row.last_name" type="text" :class="inputClass(hasError(row, 'last_name'))"></td>
              <td class="px-2 py-2 align-top"><input v-model="row.birth_date" type="date" :class="inputClass(false)"></td>
              <td class="px-2 py-2 align-top"><input v-model="row.email" type="email" :class="inputClass(hasError(row, 'email'))"></td>
              <td class="px-2 py-2 align-top"><input v-model="row.phone" type="text" :class="inputClass(hasError(row, 'phone'))"></td>
              <td class="px-2 py-2 align-top"><input v-model="row.guardian1_first_name" type="text" :class="inputClass(false)"></td>
              <td class="px-2 py-2 align-top"><input v-model="row.guardian1_last_name" type="text" :class="inputClass(false)"></td>
              <td class="px-2 py-2 align-top"><input v-model="row.license_number" type="text" :class="inputClass(hasError(row, 'license_number'))"></td>
              <td class="px-2 py-2 align-top"><input v-model="row.medical_exam_date" type="date" :class="inputClass(false)"></td>
              <td class="px-2 py-2 align-top"><input v-model="row.street" type="text" :class="inputClass(false)"></td>
              <td class="px-2 py-2 align-top"><input v-model="row.building_number" type="text" :class="inputClass(hasError(row, 'building_number'))"></td>
              <td class="px-2 py-2 align-top"><input v-model="row.postal_code" type="text" placeholder="00-000" :class="inputClass(hasError(row, 'postal_code'))"></td>
              <td class="px-2 py-2 align-top"><input v-model="row.city" type="text" :class="inputClass(hasError(row, 'city'))"></td>
              <td class="px-2 py-2 align-top"><input v-model="row.school_name" type="text" :class="inputClass(false)"></td>
              <td class="px-2 py-2 align-top">
                <button
                  type="button"
                  class="mt-1 text-gray-400 hover:text-red-600"
                  :title="t('playerImport.removeRow')"
                  @click="removeRow(row)"
                >
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/>
                  </svg>
                </button>
              </td>
            </tr>
            <tr v-if="!visibleRows.length">
              <td colspan="18" class="px-4 py-6 text-center text-gray-500">{{ t('common.noData') }}</td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>

      <!-- Submit -->
      <div class="mt-4 bg-white border-2 border-gray-300 rounded shadow-sm p-6 flex flex-col items-end text-right">
        <label class="flex items-start gap-2 text-sm text-gray-700 mb-4">
          <input v-model="sendInvitations" type="checkbox" class="w-4 h-4 mt-0.5">
          <span>
            {{ t('playerImport.sendInvitations') }}
            <span class="text-gray-500">({{ t('playerImport.invitationsCount', { count: invitationsCount }) }})</span>
          </span>
        </label>
        <p v-if="errorsCount" class="text-sm text-red-600 mb-3">{{ t('playerImport.fixErrors', { count: errorsCount }) }}</p>
        <button
          type="button"
          :disabled="importing || errorsCount > 0 || !reviewRows.length"
          class="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 border-2 border-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
          @click="submitImport"
        >
          {{ importing ? t('playerImport.importing') : t('playerImport.import', { count: reviewRows.length }) }}
        </button>
      </div>
    </div>

    <!-- Bulk gender modal -->
    <div
      v-if="showGenderModal"
      class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      @click.self="showGenderModal = false"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div class="p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ t('playerImport.bulkGender') }} ({{ selectedRows.length }})</h3>
          <select
            v-model="bulkGender"
            class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
          >
            <option value="">{{ t('playerImport.choose') }}</option>
            <option value="M">{{ t('players.male') }}</option>
            <option value="W">{{ t('players.female') }}</option>
          </select>
          <div class="mt-6 flex justify-end gap-2">
            <button
              class="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200 border-2 border-gray-300"
              @click="showGenderModal = false"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              :disabled="!bulkGender"
              class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 border-2 border-blue-600 disabled:opacity-50"
              @click="saveBulkGender"
            >
              {{ t('common.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk category modal -->
    <div
      v-if="showCategoryModal"
      class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      @click.self="showCategoryModal = false"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div class="p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ t('playerImport.bulkCategory') }} ({{ selectedRows.length }})</h3>
          <select
            v-model="bulkCategory"
            class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
          >
            <option value="">{{ t('playerImport.noCategory') }}</option>
            <option v-for="category in categories" :key="category.uuid" :value="category.uuid">{{ category.name }}</option>
          </select>
          <div class="mt-6 flex justify-end gap-2">
            <button
              class="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200 border-2 border-gray-300"
              @click="showCategoryModal = false"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 border-2 border-blue-600"
              @click="saveBulkCategory"
            >
              {{ t('common.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
